import { memo } from "react";
import { Eye, ChevronDown, ChevronUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FilePreviewInline } from "@/components/file-preview/FilePreviewInline";
import { QuestionnaireAttachmentInfo } from "./QuestionnaireAttachmentInfo";
import type { AttachmentItem } from "../../../hooks/attachment";

interface QuestionnaireAttachmentItemProps {
  item: AttachmentItem;
  isExpanded: boolean;
  url?: string;
  disabled: boolean;
  onRemove: () => void;
  onPreview: () => void;
  onToggleExpand: () => void;
}

export const QuestionnaireAttachmentItem = memo(
  function QuestionnaireAttachmentItem({
    item,
    isExpanded,
    url,
    disabled,
    onRemove,
    onPreview,
    onToggleExpand,
  }: QuestionnaireAttachmentItemProps) {
    const { field, meta } = item;

    return (
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm min-w-0">
        <div className="flex flex-col gap-0.5 px-3 py-1 min-w-0 sm:grid sm:grid-cols-[1fr_auto_auto] sm:items-center">
          <div className="overflow-x-auto pt-1 min-w-0">
            <span
              className="text-sm font-medium text-slate-900 whitespace-nowrap block"
              title={field.filename}
            >
              {field.filename}
            </span>
          </div>
          <div className="flex min-w-0 items-center justify-between gap-2 sm:contents">
            <div className="min-w-0 flex items-center">
              {meta && (
                <QuestionnaireAttachmentInfo
                  confirmed_at={meta.confirmed_at}
                  size={meta.size}
                  filePath={field.filename}
                />
              )}
            </div>
            <div className="flex shrink-0 items-center gap-0">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 sm:h-6 sm:w-6 text-red-500 hover:bg-red-50 hover:text-red-600 touch-manipulation"
                onClick={onRemove}
                disabled={disabled}
              >
                <X className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 sm:h-6 sm:w-6 touch-manipulation"
                onClick={onPreview}
                disabled={!url}
              >
                <Eye className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 sm:h-6 sm:w-6 touch-manipulation"
                onClick={onToggleExpand}
                disabled={!url}
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                ) : (
                  <ChevronDown className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                )}
              </Button>
            </div>
          </div>
        </div>
        {isExpanded && url && (
          <div className="border-t border-slate-100 px-4 py-3">
            <FilePreviewInline fileUrl={url} fileName={field.filename} />
          </div>
        )}
      </div>
    );
  },
);
